import { useUserConfig } from '@littlebird/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { backendApis, type ChannelPersonaUpdate } from '@/backendApis';
import { withTimeout } from '@/lib/promise';

import type { MessagingChannelType } from './msg-channels.types';

const LINK_CODE_REQUEST_TIMEOUT_MS = 10_000;

export function useChannelChatVisibility() {
  const userConfig = useUserConfig();
  return (visible: boolean) => {
    userConfig.chat.set('show_messaging_channel_chats', visible);
  };
}

export const msgChannelKeys = {
  all: ['messagingChannels'] as const,
  connection: (channelType: MessagingChannelType) =>
    [...msgChannelKeys.all, channelType, 'connection'] as const,
  persona: () => [...msgChannelKeys.all, 'persona'] as const,
};

export function useChannelConnectionQuery(
  channelType: MessagingChannelType,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: msgChannelKeys.connection(channelType),
    queryFn: () => backendApis.messagingChannels.getConnection(channelType),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval ?? false,
    retry: false,
  });
}

export function useDisconnectChannelMutation(
  channelType: MessagingChannelType
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      backendApis.messagingChannels.deleteConnection(channelType),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: msgChannelKeys.connection(channelType),
      });
    },
  });
}

export function useChannelLinkCodeMutation(channelType: MessagingChannelType) {
  return useMutation({
    mutationFn: () =>
      withTimeout(
        backendApis.messagingChannels.createLinkCode(channelType),
        LINK_CODE_REQUEST_TIMEOUT_MS
      ),
  });
}

export function useChannelPersonaQuery() {
  return useQuery({
    queryKey: msgChannelKeys.persona(),
    queryFn: () => backendApis.messagingChannels.getPersona(),
    retry: false,
  });
}

export function useUpdateChannelPersonaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: ChannelPersonaUpdate) =>
      backendApis.messagingChannels.updatePersona(update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: msgChannelKeys.persona() });
    },
  });
}
